import { useCallback, useMemo } from 'react';
import { useAuth } from '../../web3/context/AuthContext';
import { ethers } from 'ethers';
import { parseUnits, parseEther } from 'viem';
import {
  prepareWriteContract,
  writeContract,
  waitForTransaction,
} from '@wagmi/core';
import toast from 'react-hot-toast';
import {
  PROXY_PAYMENT_CONTRACT_DICT as _PROXY_DICT,
  PROXY_PAYMENT_CONTRACT_ABI,
} from '../../web3/ERC20-utils/proxy-payment/ProxyPayment_Constants';
import { ERC20_ABI } from '../../web3/ERC20-utils/proxy-payment/HorusCoin_Constants';
import { signTransaction, getAllBalances } from '../../web3/utils/utils';
import { ChainsObjectsArr } from '../../../chainsStaticObject/chainsStaticData';

const PROXY_PAYMENT_CONTRACT_DICT: Record<string, Record<string, string>> = _PROXY_DICT;
const NETWORK_TYPE = process.env.REACT_APP_NETWORK_TYPE || 'TESTNET';

interface SendPaymentResult {
  txHash: string;
}

interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  balance: string;
  logo?: string;
}

/**
 * Calls batchTrasnfer via wagmi (non-custodial) and returns the raw tx hash.
 */
async function transferByWagmiWithHash(
  walletAddress: string,
  recipient: string,
  tokenAddress: string,
  amount: string,
  chainKey: string,
  decimals: number,
): Promise<string> {
  const contractAddr = PROXY_PAYMENT_CONTRACT_DICT[chainKey]?.[NETWORK_TYPE];
  if (!contractAddr) throw new Error(`No ProxyPayment contract for ${chainKey}`);

  const isNative = tokenAddress === ethers.constants.AddressZero;
  const parsedAmount = parseUnits(amount, decimals);

  // ERC-20: approve first
  if (!isNative) {
    const approveConfig = await prepareWriteContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [contractAddr, parsedAmount],
    });
    const approveResult = await writeContract(approveConfig);
    await waitForTransaction({ hash: approveResult.hash });
  }

  // batchTrasnfer (note: contract function name has a typo in the ABI)
  const config = await prepareWriteContract({
    address: contractAddr as `0x${string}`,
    abi: PROXY_PAYMENT_CONTRACT_ABI,
    functionName: 'batchTrasnfer',
    args: [walletAddress, tokenAddress, [recipient], parsedAmount],
    value: isNative ? parseEther(amount) : BigInt(0),
  });

  const result = await writeContract(config);
  await waitForTransaction({ hash: result.hash });
  return result.hash;
}

/**
 * Calls batchTrasnfer via backend signing (custodial) and returns the tx hash.
 */
async function transferCustodialWithHash(
  email: string,
  walletAddress: string,
  walletIndex: number,
  recipient: string,
  tokenAddress: string,
  amount: string,
  chainKey: string,
  decimals: number,
  password: string,
): Promise<string> {
  const contractAddr = PROXY_PAYMENT_CONTRACT_DICT[chainKey]?.[NETWORK_TYPE];
  if (!contractAddr) throw new Error(`No ProxyPayment contract for ${chainKey}`);

  const isNative = tokenAddress === ethers.constants.AddressZero;
  const parsedAmount = ethers.utils.parseUnits(amount, decimals);
  const txs: any[] = [];

  // ERC-20: approve first
  if (!isNative) {
    txs.push({
      contractAddress: tokenAddress,
      abi: ERC20_ABI,
      functionName: 'approve',
      params: [contractAddr, parsedAmount],
    });
  }

  txs.push({
    contractAddress: contractAddr,
    abi: PROXY_PAYMENT_CONTRACT_ABI,
    functionName: 'batchTrasnfer',
    params: [walletAddress, tokenAddress, [recipient], parsedAmount],
    value: isNative ? ethers.utils.parseEther(amount) : null,
  });

  const hash = await signTransaction(
    email,
    walletIndex,
    chainKey,
    password,
    txs,
    'Sending Payment',
  );

  if (!hash) throw new Error('Transaction failed');
  return hash;
}

export function useSendPayment() {
  const {
    loginOption,
    entityInfo,
    network,
    email,
  } = useAuth();

  const walletAddress = entityInfo?.activePublicKey || '';
  const walletIndex = entityInfo?.indexWallet ?? 0;
  const isCustodial = loginOption === 'custodial';

  // Chains that have ProxyPayment contracts deployed
  const availableChains = useMemo(() => {
    return ChainsObjectsArr.filter(
      (c: any) =>
        c.networkType === 'EVM' &&
        PROXY_PAYMENT_CONTRACT_DICT[c.chain]?.[NETWORK_TYPE]
    );
  }, []);

  const fetchTokens = useCallback(
    async (chainKey: string): Promise<TokenInfo[]> => {
      if (!walletAddress) return [];
      try {
        const balances = await getAllBalances(walletAddress, chainKey);
        if (!Array.isArray(balances)) return [];

        // Find the chain object for native token info
        const chainObj = ChainsObjectsArr.find((c: any) => c.chain === chainKey);
        const nativeToken: TokenInfo = {
          address: ethers.constants.AddressZero,
          symbol: chainObj?.nativeToken || 'ETH',
          name: chainObj?.nativeToken || 'Native Token',
          decimals: chainObj?.decimals || 18,
          balance: '0',
          logo: chainObj?.logo,
        };

        const tokens: TokenInfo[] = [nativeToken];

        for (const item of balances) {
          // Handle various API formats (Uniblock / Alchemy)
          const addr = item.contractAddress || item.token_address || item.address;
          if (!addr || addr === ethers.constants.AddressZero) {
            // Update native balance
            nativeToken.balance = item.balance || item.tokenBalance || '0';
            continue;
          }
          tokens.push({
            address: addr,
            symbol: item.symbol || item.tokenSymbol || '???',
            name: item.name || item.tokenName || '',
            decimals: Number(item.decimals || item.tokenDecimals || 18),
            balance: item.balance || item.tokenBalance || '0',
            logo: item.logo || item.thumbnail || undefined,
          });
        }
        return tokens;
      } catch (err) {
        console.error('[useSendPayment] fetchTokens failed:', err);
        return [];
      }
    },
    [walletAddress],
  );

  const sendPayment = useCallback(
    async (
      recipientAddress: string,
      amount: string,
      tokenAddress: string,
      chainKey: string,
      decimals: number,
      password?: string,
    ): Promise<SendPaymentResult> => {
      if (!ethers.utils.isAddress(recipientAddress)) {
        throw new Error('Invalid recipient address');
      }

      if (isCustodial) {
        if (!password) throw new Error('Password required for custodial transfer');
        const txHash = await transferCustodialWithHash(
          email,
          walletAddress,
          walletIndex,
          recipientAddress,
          tokenAddress,
          amount,
          chainKey,
          decimals,
          password,
        );
        return { txHash };
      } else {
        const txHash = await transferByWagmiWithHash(
          walletAddress,
          recipientAddress,
          tokenAddress,
          amount,
          chainKey,
          decimals,
        );
        return { txHash };
      }
    },
    [isCustodial, email, walletAddress, walletIndex],
  );

  return {
    sendPayment,
    availableChains,
    fetchTokens,
    isCustodial,
    walletAddress,
    currentChain: network,
  };
}
